import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText,
  ExternalLink,
  ChevronDown,
  ChevronRight,
  Store,
  ShoppingCart
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { getCurrentUserOrganizationId } from '../../lib/stores/referenceDataCache';
import type { UnifiedOrder } from '../../lib/types/orders';
import { UNIFIED_ORDER_STATUS_CONFIG } from '../../lib/types/orders';
import { orderDisplayNumber, ordersSectionUrl } from '../../lib/utils/unifiedOrderQuery';
import { storeBadgesFor, useStoreOrderStatuses } from '../../lib/commerce/useStoreOrderStatuses';
import { StoreStatusBadges } from '../magazzino/orders/StoreStatusBadges';

interface ClientOrdersSectionProps {
  clientId: string;
}

// Period filter options
type PeriodFilter = 'all' | '30d' | '90d' | 'year';

const PERIOD_OPTIONS: { value: PeriodFilter; label: string }[] = [
  { value: 'all', label: 'Tutti' },
  { value: '30d', label: 'Ultimi 30gg' },
  { value: '90d', label: 'Ultimi 90gg' },
  { value: 'year', label: "Quest'anno" }
];

// Ordine degli stati come nella pagina ordini (UNIFIED_ORDER_STATUS_CONFIG)
const STATUS_ORDER = [
  'draft',
  'confirmed',
  'pending',
  'processing',
  'on-hold',
  'prepared',
  'shipped',
  'invoiced',
  'completed',
  'cancelled',
  'refunded',
  'failed',
  'trash'
];

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('it-IT', {
    day: '2-digit',
    month: 'short'
  });
};

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2
  }).format(amount);
};

const getDateFromFilter = (period: PeriodFilter): string | null => {
  const now = new Date();
  switch (period) {
    case '30d':
      return new Date(now.setDate(now.getDate() - 30)).toISOString();
    case '90d':
      return new Date(now.setDate(now.getDate() - 90)).toISOString();
    case 'year':
      return new Date(now.getFullYear(), 0, 1).toISOString();
    default:
      return null;
  }
};

export function ClientOrdersSection({ clientId }: ClientOrdersSectionProps) {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<UnifiedOrder[]>([]);
  // Stato nel negozio (WooCommerce / Shopify) degli ordini del motore
  const statiNegozio = useStoreOrderStatuses(orders);
  const [totalOrderCount, setTotalOrderCount] = useState(0); // Count without filters
  const [availableStatuses, setAvailableStatuses] = useState<string[]>([]); // Stati disponibili per questo cliente
  const [loading, setLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(true); // Storico subito visibile, anche senza ordini
  const [periodFilter, setPeriodFilter] = useState<PeriodFilter>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showPeriodDropdown, setShowPeriodDropdown] = useState(false);

  // Fetch total count and available statuses (without filters)
  const fetchTotalCountAndStatuses = useCallback(async () => {
    if (!clientId) return;

    const organizationId = await getCurrentUserOrganizationId();
    if (!organizationId) return;

    try {
      // Fetch all orders for this client to get count and unique statuses
      const { data, error } = await supabase
        .from('v_all_orders')
        .select('status')
        .eq('organization_id', organizationId)
        .eq('client_id', clientId);

      if (error) throw error;

      const orderData = data || [];
      setTotalOrderCount(orderData.length);

      // Extract unique statuses
      const uniqueStatuses = [...new Set(orderData.map(o => o.status))];
      setAvailableStatuses(uniqueStatuses);
    } catch (error) {
      console.error('Error fetching order count:', error);
    }
  }, [clientId]);

  // Fetch orders for this client (with filters)
  const fetchOrders = useCallback(async () => {
    if (!clientId) return;

    const organizationId = await getCurrentUserOrganizationId();
    if (!organizationId) return;

    try {
      setLoading(true);

      let query = supabase
        .from('v_all_orders')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('client_id', clientId)
        .order('order_date', { ascending: false, nullsFirst: false });

      // Apply date filter
      const dateFrom = getDateFromFilter(periodFilter);
      if (dateFrom) {
        query = query.gte('order_date', dateFrom);
      }

      // Apply status filter
      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      const { data, error } = await query;

      if (error) throw error;

      setOrders((data || []) as UnifiedOrder[]);
    } catch (error) {
      console.error('Error fetching client orders:', error);
    } finally {
      setLoading(false);
    }
  }, [clientId, periodFilter, statusFilter]);

  // Fetch total count and statuses on mount
  useEffect(() => {
    fetchTotalCountAndStatuses();
  }, [fetchTotalCountAndStatuses]);

  // Fetch orders only when expanded
  useEffect(() => {
    if (isExpanded) {
      fetchOrders();
    }
  }, [fetchOrders, isExpanded]);

  // Calculate total for the period
  const periodTotal = useMemo(() => {
    return orders.reduce((sum, order) => sum + (order.total || 0), 0);
  }, [orders]);

  // Ordina gli stati disponibili secondo l'ordine definito
  const sortedAvailableStatuses = useMemo(() => {
    return availableStatuses.sort((a, b) => {
      const indexA = STATUS_ORDER.indexOf(a);
      const indexB = STATUS_ORDER.indexOf(b);
      // Se non trovato nell'ordine, metti in fondo
      const orderA = indexA === -1 ? 999 : indexA;
      const orderB = indexB === -1 ? 999 : indexB;
      return orderA - orderB;
    });
  }, [availableStatuses]);

  // Get status config
  const getStatusConfig = (status: string) => {
    return UNIFIED_ORDER_STATUS_CONFIG[status] || {
      label: status,
      color: 'text-gray-700',
      bgColor: 'bg-gray-100'
    };
  };

  // Handle order click - navigate to order detail
  const handleOrderClick = (order: UnifiedOrder) => {
    // Il Magazzino legge ?section= (con ?tab=ordini si apriva la Dashboard)
    navigate(ordersSectionUrl({ orderId: order.id, channel: order.channel }));
  };

  // Get current period label
  const currentPeriodLabel = PERIOD_OPTIONS.find(p => p.value === periodFilter)?.label || 'Tutti';

  // Non mostrare la sezione se non ci sono ordini
  if (totalOrderCount === 0) {
    return <p className="py-3 text-sm text-gray-500">Nessun ordine per questa anagrafica.</p>;
  }

  return (
    <div className="space-y-2">
      {/* Header - cliccabile per espandere/collassare */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between py-2 hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/[0.03] rounded transition-colors -mx-1 px-1"
      >
        <div className="flex items-center space-x-2">
          {isExpanded ? (
            <ChevronDown className="h-3 w-3 text-[#787774] dark:text-gray-500" />
          ) : (
            <ChevronRight className="h-3 w-3 text-[#787774] dark:text-gray-500" />
          )}
          <ShoppingCart className="h-4 w-4 text-[#787774] dark:text-gray-500" />
          <h3 className="text-xs font-medium text-[#787774] dark:text-gray-400 uppercase tracking-wide">
            Ordini
          </h3>
          <span className="text-xs text-[#787774] dark:text-gray-500">
            ({totalOrderCount})
          </span>
        </div>
      </button>

      {/* Contenuto espanso */}
      {isExpanded && (
        <>
          {/* Period filter dropdown */}
          <div className="flex items-center justify-end">
            <div className="relative">
              <button
                onClick={() => setShowPeriodDropdown(!showPeriodDropdown)}
                className="flex items-center gap-1 text-xs text-[#787774] dark:text-gray-400 hover:text-[#37352F] dark:hover:text-white px-2 py-1 rounded hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/[0.08] transition-colors"
              >
                {currentPeriodLabel}
                <ChevronDown className="h-3 w-3" />
              </button>

              {showPeriodDropdown && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowPeriodDropdown(false)}
                  />
                  <div className="absolute right-0 top-full mt-1 z-20 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 min-w-[120px]">
                    {PERIOD_OPTIONS.map(option => (
                      <button
                        key={option.value}
                        onClick={() => {
                          setPeriodFilter(option.value);
                          setShowPeriodDropdown(false);
                        }}
                        className={`w-full text-left px-3 py-1.5 text-xs hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/[0.08] transition-colors ${
                          periodFilter === option.value
                            ? 'text-[#337EA9] dark:text-[#529CCA] font-medium'
                            : 'text-[#37352F] dark:text-white'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Status chips filter - mostra solo stati disponibili */}
          <div className="flex flex-wrap gap-1.5 pb-1">
            {/* Sempre mostra "Tutti" */}
            <button
              onClick={() => setStatusFilter('all')}
              className={`px-2 py-0.5 text-xs rounded-full transition-colors ${
                statusFilter === 'all'
                  ? 'bg-[#337EA9] text-white'
                  : 'bg-[rgba(55,53,47,0.08)] dark:bg-white/[0.08] text-[#787774] dark:text-gray-400 hover:bg-[rgba(55,53,47,0.12)] dark:hover:bg-white/[0.12]'
              }`}
            >
              Tutti
            </button>
            {/* Mostra solo stati presenti negli ordini del cliente, nell'ordine corretto */}
            {sortedAvailableStatuses.map(status => {
              const statusConfig = getStatusConfig(status);
              return (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  className={`px-2 py-0.5 text-xs rounded-full transition-colors ${
                    statusFilter === status
                      ? 'bg-[#337EA9] text-white'
                      : 'bg-[rgba(55,53,47,0.08)] dark:bg-white/[0.08] text-[#787774] dark:text-gray-400 hover:bg-[rgba(55,53,47,0.12)] dark:hover:bg-white/[0.12]'
                  }`}
                >
                  {statusConfig.label}
                </button>
              );
            })}
          </div>

          {/* Total for period */}
          {orders.length > 0 && (
            <div className="flex items-center justify-between py-1.5 px-2 bg-[rgba(55,53,47,0.04)] dark:bg-white/[0.03] rounded">
              <span className="text-xs text-[#787774] dark:text-gray-400">
                Totale {periodFilter !== 'all' ? 'nel periodo' : ''}
              </span>
              <span className="text-sm font-medium text-[#37352F] dark:text-white">
                {formatCurrency(periodTotal)}
              </span>
            </div>
          )}

          {/* Loading state */}
          {loading ? (
            <div className="py-4 flex justify-center">
              <div className="w-5 h-5 border-2 border-[#337EA9] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : orders.length === 0 ? (
            <p className="py-2 px-2 text-sm text-[#787774] dark:text-gray-400 italic">
              {statusFilter !== 'all' || periodFilter !== 'all'
                ? 'Nessun ordine trovato con questi filtri'
                : 'Nessun ordine'}
            </p>
          ) : (
            <div className="space-y-0.5">
              {orders.slice(0, 5).map(order => {
                const statusConfig = getStatusConfig(order.status);
                return (
                  <div
                    key={order.id}
                    className="py-2 px-2 hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/[0.03] rounded transition-colors cursor-pointer"
                    onClick={() => handleOrderClick(order)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2 min-w-0 flex-1">
                        {/* Channel icon */}
                        {order.channel === 'woocommerce' ? (
                          <Store className="h-4 w-4 text-purple-500 flex-shrink-0" />
                        ) : (
                          <FileText className="h-4 w-4 text-blue-500 flex-shrink-0" />
                        )}
                        <span className="text-sm text-[#37352F] dark:text-white truncate">
                          {orderDisplayNumber(order)}
                        </span>
                      </div>
                      {(() => {
                        // Ordini dei negozi: lo stato come nel negozio; manuali: lo stato di Flusia
                        const badges = storeBadgesFor(order, statiNegozio);
                        return badges
                          ? <span className="flex-shrink-0 ml-2"><StoreStatusBadges badges={badges} size="xs" title="Stato nel negozio" /></span>
                          : (
                            <span className={`text-xs px-1.5 py-0.5 rounded flex-shrink-0 ml-2 ${statusConfig.bgColor} ${statusConfig.color} dark:bg-opacity-20`}>
                              {statusConfig.label}
                            </span>
                          );
                      })()}
                    </div>
                    <div className="flex items-center justify-between ml-6 mt-0.5">
                      <span className="text-xs text-[#787774] dark:text-gray-400">
                        {formatCurrency(order.total)}
                      </span>
                      <span className="text-xs text-[#787774] dark:text-gray-500">
                        {formatDate(order.order_date || order.created_at)}
                      </span>
                    </div>
                  </div>
                );
              })}

              {/* Show more link */}
              {orders.length > 5 && (
                <button
                  onClick={() => navigate(ordersSectionUrl({ clientId }))}
                  className="w-full py-2 px-2 text-xs text-[#337EA9] dark:text-[#529CCA] hover:underline flex items-center justify-center gap-1"
                >
                  Vedi tutti ({orders.length})
                  <ExternalLink className="h-3 w-3" />
                </button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
